import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';
import go_n from '../../Images/go_n.gif';
import go_o from '../../Images/go_o.gif';
import close_n from '../../Images/close_n.gif';
import close_o from '../../Images/close_o.gif';
import popup_bg from '../../Common/Images/ImagesForAlert/popup_bg1.png';
import alert_close_n from '../../Common/Images/ImagesForAlert/alert_close_n.png';
import alert_close_o from '../../Common/Images/ImagesForAlert/alert_close_o.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface SalesEntry {
    ActivityStatus: string;
    DistributorName: string;
    Surname: string;
    ApprovedDate: string;
    EducationalConsultant: string;
    State: string;
    PurchaseOrder: string;
    FinanceSaleABCDStatus: string;
    PaymentMethod: string;
    CrelogixClientID: string;
    Packages: string;
    PackageAmount: number;
    TaxAmount: number;
    TotalAmount_IncTax: number;
}

interface DropdownOption {
    value: string;
    label: string;
}

interface Props {
    onBack?: () => void;
}

const styles = {
    fullScreenWindow: {
        position: 'fixed' as 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'Azure',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column' as 'column'
    },
    popupHeader: {
        backgroundImage: `url(${popup_bg})`,
        backgroundSize: '100% 100%',
        height: '30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: '10px',
        flexShrink: 0
    },
    popupTitle: {
        color: 'white',
        fontWeight: 'bold' as 'bold',
        fontSize: '11pt',
        fontFamily: 'Arial'
    },
    container: {
        position: 'relative' as 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as 'column',
        fontFamily: 'Arial, sans-serif',
        fontSize: '10pt',
        backgroundColor: 'Azure',
        overflow: 'hidden'
    },
    headerSection: {
        backgroundColor: 'Azure',
        flexShrink: 0,
        padding: '10px 15px'
    },
    topBanner: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginBottom: '10px',
        gap: "30%"
    },
    reportRunText: {
        fontSize: '10pt',
        fontWeight: 'bold' as 'bold'
    },
    pageTitle: {
        fontSize: '20pt',
        fontWeight: 'bold' as 'bold',
        color: 'Black',
        textAlign: 'center' as 'center',
    },
    controlsTable: {
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        backgroundColor: '#c1e0ff',
        border: '2px solid #80A0FF'
    },
    controlCell: {
        padding: '5px',
        borderRight: '2px solid #80A0FF',
        verticalAlign: 'top' as 'top'
    },
    label: {
        fontWeight: 'bold' as 'bold',
        display: 'block',
        marginBottom: '3px'
    },
    input: {
        border: '1px solid #003385',
        padding: '2px',
        fontSize: '12pt',
        fontFamily: 'Calibri',
        width: '100%',
        maxWidth: '140px'
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
        padding: '5px',
        margin: '0 10px'
    },
    dataTable: {
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        fontSize: '12pt'
    },
    th: {
        backgroundColor: '#c7ddfd',
        color: '#2859ac',
        fontSize: '12pt',
        padding: '5px',
        border: '1px solid #80a0ff',
        textAlign: 'left' as 'left',
        position: 'sticky' as 'sticky',
        top: 0,
        fontWeight: "normal"
    },
    td: {
        padding: '5px',
        borderRight: '1px solid #a3c0e8',
        borderLeft: '1px solid #a3c0e8',
        fontSize: '12pt'
    },
    footer: {
        padding: '10px 15px',
        backgroundColor: 'Azure',
        borderTop: '1px solid #ccc',
        fontSize: '10pt',
        flexShrink: 0
    }
};

const KMDistributorSalesReport: React.FC<Props> = ({ onBack }) => {
    const [searchParams] = useSearchParams();
    const calledFrom = searchParams.get('CalledFrom') || '';

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const [fromDate, setFromDate] = useState(formatDate(firstDay));
    const [toDate, setToDate] = useState(formatDate(today));
    const [countries, setCountries] = useState<DropdownOption[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [distributors, setDistributors] = useState<DropdownOption[]>([{ value: 'ALL', label: 'ALL' }]);
    const [selectedDistributor, setSelectedDistributor] = useState<string>('ALL');
    const [states, setStates] = useState<DropdownOption[]>([{ value: 'ALL', label: 'ALL' }]);
    const [selectedState, setSelectedState] = useState<string>('ALL');
    const [selectedStatus, setSelectedStatus] = useState<string>('ALL');

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<SalesEntry[]>([]);
    const [totalSales, setTotalSales] = useState(0);
    const [totalSalesValue, setTotalSalesValue] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

    const [isGoHovered, setIsGoHovered] = useState(false);
    const [isCloseHovered, setIsCloseHovered] = useState(false);

    useEffect(() => {
        const fetchCountries = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}api/sales-report/filters/`);
                if (response.ok) {
                    const data = await response.json();
                    const cOpts = data.countries.map((c: string) => ({ value: c, label: c }));
                    setCountries(cOpts);
                    if (cOpts.length > 0) setSelectedCountry(cOpts[0].value);
                }
            } catch (err) { console.error("Error fetching countries", err); }
            finally { setLoading(false); }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        if (!selectedCountry) return;
        const fetchDeps = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}api/sales-report/filters/?country=${encodeURIComponent(selectedCountry)}`);
                if (response.ok) {
                    const data = await response.json();

                    const dOpts = data.distributors.map((d: any) => ({ value: d.schoolcode, label: d.name }));
                    dOpts.unshift({ value: 'ALL', label: 'ALL' });
                    setDistributors(dOpts);
                    setSelectedDistributor('ALL');

                    const sOpts = data.states.map((s: string) => ({ value: s, label: s }));
                    sOpts.unshift({ value: 'ALL', label: 'ALL' });
                    setStates(sOpts);
                    setSelectedState('ALL');
                }
            } catch (err) { console.error("Error fetching dependencies", err); }
        };
        fetchDeps();
    }, [selectedCountry]);

    const handleGoClick = async () => {
        if (!selectedCountry) {
            setAlertInfo({ show: true, message: 'Please select a Country.' });
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                fromDate,
                toDate,
                country: selectedCountry,
                distributor: selectedDistributor,
                state: selectedState,
                status: selectedStatus
            });

            const response = await fetch(`${API_BASE_URL}api/sales-report/generate/?${params.toString()}`);
            if (!response.ok) throw new Error("Failed to load report data.");

            const data = await response.json();
            setReportData(data.data || []);
            setTotalSales(data.totalSales || 0);
            setTotalSalesValue(data.totalSalesValue || 0);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getCurrencySymbol = (country: string) => {
        switch (country) {
            case 'Australia': return 'A$';
            case 'Malaysia': return 'RM';
            case 'UK':
            case 'UK - Kinetic': return '£';
            case 'Canada':
            case 'USA': return '$';
            default: return '';
        }
    };

    const groupedData = reportData.reduce((acc, curr) => {
        const stat = curr.ActivityStatus;
        if (!acc[stat]) acc[stat] = [];
        acc[stat].push(curr);
        return acc;
    }, {} as Record<string, SalesEntry[]>);

    const getRowColor = (status: string) => {
        if (status === 'P') return '#2DD4D0';
        if (status === 'C') return 'red';
        return 'inherit';
    };

    return (
        <div style={styles.fullScreenWindow}>
            <div style={styles.popupHeader}>
                <span style={styles.popupTitle}>Distributor Sales Report</span>
                <img
                    src={alert_close_n}
                    alt="Close"
                    style={{ cursor: 'pointer', height: '30px', width: '62px' }}
                    onMouseOver={(e) => e.currentTarget.src = alert_close_o}
                    onMouseOut={(e) => e.currentTarget.src = alert_close_n}
                    onClick={() => {
                        if (onBack) onBack();
                        else if (calledFrom) window.close();
                        else window.history.back();
                    }}
                />
            </div>
            <div style={styles.container}>
                <div style={styles.headerSection}>
                    <div style={styles.topBanner}>
                        <div style={styles.reportRunText}>
                            REPORT RUN ON{" "}
                            <span style={{ fontWeight: "normal", color: "black" }}>
                                {today.toLocaleDateString("en-GB")}
                            </span>
                        </div>
                        <div style={{ ...styles.pageTitle, textAlign: "center" }}>Sales Report</div>
                    </div>

                    <table style={styles.controlsTable}>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.controlCell, borderRight: 'none' }}>
                                    <span style={styles.label}>From:</span>
                                    <input type="date" style={styles.input} value={fromDate} onChange={e => setFromDate(e.target.value)} />
                                </td>
                                <td style={{ ...styles.controlCell, borderRight: 'none' }}>
                                    <span style={styles.label}>To:</span>
                                    <input type="date" style={styles.input} value={toDate} onChange={e => setToDate(e.target.value)} />
                                </td>
                                <td style={{ ...styles.controlCell, borderRight: 'none' }}>
                                    <span style={styles.label}>Country:</span>
                                    <select style={styles.input} value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}>
                                        {countries.map((c, i) => <option key={i} value={c.value}>{c.label}</option>)}
                                    </select>
                                </td>
                                <td style={{ ...styles.controlCell, borderRight: 'none' }}>
                                    <span style={styles.label}>Distributor:</span>
                                    <select style={styles.input} value={selectedDistributor} onChange={e => setSelectedDistributor(e.target.value)}>
                                        {distributors.map((d, i) => <option key={i} value={d.value}>{d.label}</option>)}
                                    </select>
                                </td>
                                <td style={{ ...styles.controlCell, borderRight: 'none' }}>
                                    <span style={styles.label}>State:</span>
                                    <select style={styles.input} value={selectedState} onChange={e => setSelectedState(e.target.value)}>
                                        {states.map((s, i) => <option key={i} value={s.value}>{s.label}</option>)}
                                    </select>
                                </td>
                                <td style={styles.controlCell}>
                                    <span style={styles.label}>Status:</span>
                                    <select style={styles.input} value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
                                        <option value="ALL">ALL</option>
                                        <option value="A">Active</option>
                                        <option value="P">Pending</option>
                                        <option value="C">Cancelled</option>
                                    </select>
                                </td>
                                <td style={{ ...styles.controlCell, textAlign: 'center', verticalAlign: 'middle' }}>
                                    <img
                                        src={isGoHovered ? go_o : go_n}
                                        alt="Go"
                                        onClick={handleGoClick}
                                        onMouseEnter={() => setIsGoHovered(true)}
                                        onMouseLeave={() => setIsGoHovered(false)}
                                        style={{ cursor: 'pointer', width: '80px' }}
                                    />
                                </td>
                                <td style={{ ...styles.controlCell, textAlign: 'center', verticalAlign: 'middle', borderRight: 'none' }}>
                                    <img
                                        src={isCloseHovered ? close_o : close_n}
                                        alt="Close"
                                        onClick={() => {
                                            if (onBack) onBack();
                                            else if (calledFrom) window.close();
                                            else window.history.back();
                                        }}
                                        onMouseEnter={() => setIsCloseHovered(true)}
                                        onMouseLeave={() => setIsCloseHovered(false)}
                                        style={{ cursor: 'pointer', width: '80px' }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {loading && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Loading /></div>}
                {error && <div style={{ color: 'red', padding: '10px' }}>{error}</div>}

                <div style={styles.tableContainer}>
                    {reportData.length > 0 && (
                        <table style={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Count</th>
                                    <th style={styles.th}>Distributor/Office</th>
                                    <th style={styles.th}>Surname</th>
                                    <th style={styles.th}>Approved Date</th>
                                    <th style={styles.th}>Educational<br />Consultant</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Purchase<br />Order #</th>
                                    <th style={styles.th}>Finance Incl<br />A B C D</th>
                                    <th style={styles.th}>Payment Method</th>
                                    <th style={styles.th}>Crelogix<br />Client ID</th>
                                    <th style={styles.th}>Package</th>
                                    <th style={{ ...styles.th, textAlign: 'right' }}>PackageAmount</th>
                                    <th style={{ ...styles.th, textAlign: 'right' }}>Tax Amount</th>
                                    <th style={{ ...styles.th, textAlign: 'right' }}>Total<br />Amount<br />incl tax</th>
                                </tr>
                            </thead>
                            <tbody>
                                {['A', 'P', 'C'].map(statusGroup => {
                                    const rows = groupedData[statusGroup];
                                    if (!rows || rows.length === 0) return null;

                                    let subPkgAmt = 0;
                                    let subTaxAmt = 0;
                                    let subTotalAmt = 0;

                                    return (
                                        <React.Fragment key={statusGroup}>
                                            {rows.map((row, idx) => {
                                                const pkgAmt = Number(row.PackageAmount) || 0;
                                                const taxAmt = Number(row.TaxAmount) || 0;
                                                const totalAmt = Number(row.TotalAmount_IncTax) || 0;

                                                subPkgAmt += pkgAmt;
                                                subTaxAmt += taxAmt;
                                                subTotalAmt += totalAmt;

                                                const rowColor = getRowColor(statusGroup);

                                                return (
                                                    <tr
                                                        key={idx}
                                                        style={{
                                                            color: rowColor,
                                                            backgroundColor: idx % 2 === 0 ? '#f4faff' : '#fff'
                                                        }}
                                                    >
                                                        <td style={styles.td}>{row.ActivityStatus}</td>
                                                        <td style={styles.td}>1</td>
                                                        <td style={styles.td}>{row.DistributorName}</td>
                                                        <td style={styles.td}>{row.Surname}</td>
                                                        <td style={styles.td}>{row.ApprovedDate ? row.ApprovedDate.split('T')[0] : ''}</td>
                                                        <td style={styles.td}>{row.EducationalConsultant}</td>
                                                        <td style={styles.td}>{row.State}</td>
                                                        <td style={styles.td}>{row.PurchaseOrder}</td>
                                                        <td style={styles.td}>{row.FinanceSaleABCDStatus}</td>
                                                        <td style={styles.td}>{row.PaymentMethod}</td>
                                                        <td style={styles.td}>{row.CrelogixClientID}</td>
                                                        <td style={styles.td} dangerouslySetInnerHTML={{ __html: row.Packages || '' }}></td>

                                                        <td style={{ ...styles.td, textAlign: 'right' }}>{pkgAmt.toFixed(2)}</td>
                                                        <td style={{ ...styles.td, textAlign: 'right' }}>{taxAmt.toFixed(2)}</td>
                                                        <td style={{ ...styles.td, textAlign: 'right' }}>{totalAmt.toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })}
                                            <tr style={{ color: getRowColor(statusGroup), fontWeight: 'bold' }}>
                                                <td style={styles.td}>Totals</td>
                                                <td style={styles.td}>{rows.length}</td>
                                                <td style={styles.td} colSpan={10}></td>
                                                <td style={{ ...styles.td, textAlign: 'right' }}>{subPkgAmt.toFixed(2)}</td>
                                                <td style={{ ...styles.td, textAlign: 'right' }}>{subTaxAmt.toFixed(2)}</td>
                                                <td style={{ ...styles.td, textAlign: 'right' }}>{subTotalAmt.toFixed(2)}</td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={styles.footer}>
                    <b>Total Sales:</b> &nbsp;
                    <span style={{ fontWeight: 'bold', fontSize: '11pt' }}>{totalSales}</span>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <b>Total Sales Value(Inc Tax):</b> &nbsp;
                    <span style={{ fontWeight: 'bold', fontSize: '11pt' }}>
                        {getCurrencySymbol(selectedCountry)} {(Number(totalSalesValue) || 0).toFixed(2)}
                    </span>
                </div>

                {alertInfo.show && (
                    <CustomAlert
                        message={alertInfo.message}
                        onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                    />
                )}
            </div>
        </div>
    );
};

export default KMDistributorSalesReport;